/**
 * Arena Results Page - The Intelligent Standard
 *
 * Cohesive, flowing results page.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ArenaPanel } from '@/components/ArenaPanel';
import { Logo, LogoAnimated } from '@/components/Logo';
import { HeroPattern } from '@/components/shared/HeroPattern';
import { ProviderLogo } from '@/components/shared/ProviderLogo';
import { apiRequest } from '@/config';

type ModelStats = {
  config_id: string;
  config_name: string;
  wins: number;
  losses: number;
  ties: number;
  both_bad: number;
  total_matches: number;
  elo: number;
};

type StatsResponse = {
  success: boolean;
  prompt: {
    text: string;
    category: string;
  };
  stats: ModelStats[];
  total_votes: number;
};

type ArenaResult = {
  id: string;
  model_config_id: string;
  model_config_name: string;
  sheet_json: any;
  compute_values: any;
};

function getModelKeyFromName(name: string): string {
  const lower = name.toLowerCase();
  if (
    lower.includes('claude') ||
    lower.includes('opus') ||
    lower.includes('sonnet')
  )
    return 'claude';
  if (lower.includes('gpt')) return 'gpt';
  if (lower.includes('gemini')) return 'gemini';
  if (lower.includes('grok')) return 'grok';
  if (lower.includes('llama') || lower.includes('meta')) return 'llama';
  if (lower.includes('kimi') || lower.includes('moonshot')) return 'kimi';
  if (lower.includes('qwen') || lower.includes('alibaba')) return 'qwen';
  return 'unknown';
}

export default function ArenaResultsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const runId = params?.id as string;

  // Back URL is always home
  const backUrl = '/';

  const [data, setData] = useState<StatsResponse | null>(null);
  const [winningResult, setWinningResult] = useState<ArenaResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRankings, setShowRankings] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);

  useEffect(() => {
    async function loadResults() {
      try {
        const statsData = await apiRequest(`/api/arena/runs/${runId}/stats`);
        setData(statsData);

        if (statsData.stats?.length > 0) {
          const winnerConfigId = statsData.stats[0].config_id;
          const resultsData = await apiRequest(
            `/api/arena/runs/${runId}/valid-results`
          );
          const winnerResult = resultsData.results?.find(
            (r: ArenaResult) => r.model_config_id === winnerConfigId
          );
          if (winnerResult) {
            setWinningResult(winnerResult);
          }
        }

        setLoading(false);
      } catch (e: any) {
        setError(e.message);
        setLoading(false);
      }
    }

    if (runId) loadResults();
  }, [runId]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#F5F0EB' }}
      >
        <div className="text-center">
          <LogoAnimated size={48} variant="default" />
          <p className="mt-4 text-sm" style={{ color: '#6B6560' }}>
            Loading results...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-8"
        style={{ background: '#F5F0EB' }}
      >
        <div className="text-center max-w-sm">
          <div className="flex justify-center mb-6">
            <Logo size={48} variant="default" />
          </div>
          <p className="text-base mb-6" style={{ color: '#8B2332' }}>
            {error || 'No results available'}
          </p>
          <Link
            to={backUrl}
            className="inline-flex px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: '#8B2332' }}
          >
            Back to Arena
          </Link>
        </div>
      </div>
    );
  }

  const { prompt, stats, total_votes } = data;
  const winner = stats[0];
  const isLongPrompt = prompt.text.length > 300;

  return (
    <div className="min-h-screen" style={{ background: '#F5F0EB' }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
      `}</style>

      {/* Hero */}
      <HeroPattern>
        {/* Home link */}
        <div className="absolute top-6 left-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
        </div>

        <div className="max-w-3xl mx-auto text-center pt-24 pb-20 px-8">
          <div className="fade-up">
            <p
              className="text-xs uppercase tracking-[0.25em] mb-8"
              style={{ color: 'rgba(255,255,255,0.4)' }}
            >
              Battle Complete
            </p>

            <div className="flex items-center justify-center gap-4 mb-5">
              <ProviderLogo
                modelKey={getModelKeyFromName(winner?.config_name || '')}
                size={48}
                showBackground={false}
              />
              <h1 className="font-serif text-4xl md:text-5xl font-medium text-white">
                {winner?.config_name.replace(' Baseline', '')}
              </h1>
            </div>

            <p className="text-base" style={{ color: 'rgba(255,255,255,0.5)' }}>
              won with {total_votes} community{' '}
              {total_votes === 1 ? 'vote' : 'votes'}
            </p>
          </div>
        </div>
      </HeroPattern>

      {/* Main content area */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Prompt */}
        <div className="mb-10 fade-up" style={{ animationDelay: '100ms' }}>
          <div
            className="flex items-start gap-4 cursor-pointer"
            onClick={() => isLongPrompt && setPromptExpanded(!promptExpanded)}
          >
            <div
              className="flex-shrink-0 w-1 rounded-full mt-1"
              style={{
                background: '#8B2332',
                height: isLongPrompt && !promptExpanded ? '60px' : 'auto',
                minHeight: '24px',
                alignSelf: 'stretch',
              }}
            />
            <div className="flex-1">
              <p
                className={`text-[15px] leading-relaxed ${isLongPrompt && !promptExpanded ? 'line-clamp-3' : ''}`}
                style={{ color: '#3A3A3A' }}
              >
                {prompt.text}
              </p>
              {isLongPrompt && (
                <button
                  className="mt-2 text-xs font-medium"
                  style={{ color: '#8B2332' }}
                >
                  {promptExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Winning Output */}
        {winningResult && (
          <div className="fade-up" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ProviderLogo
                  modelKey={getModelKeyFromName(winner?.config_name || '')}
                  size={28}
                />
                <span
                  className="font-medium text-sm"
                  style={{ color: '#1A1A1A' }}
                >
                  {winner?.config_name.replace(' Baseline', '')}
                </span>
              </div>
              <span
                className="text-xs uppercase tracking-wider"
                style={{ color: '#8B8580' }}
              >
                Winning Output
              </span>
            </div>

            <div
              className="rounded-xl overflow-hidden"
              style={{
                background: '#FFFFFF',
                border: '1px solid #E8E3DD',
                height: '450px',
              }}
            >
              <ArenaPanel
                label=""
                submission={winningResult}
                scrollId="winner-spreadsheet"
                embedded
              />
            </div>
          </div>
        )}

        {/* Rankings section */}
        <div className="mt-12 fade-up" style={{ animationDelay: '300ms' }}>
          <button
            onClick={() => setShowRankings(!showRankings)}
            className="w-full flex items-center justify-between pb-4 mb-4"
            style={{ borderBottom: '1px solid #DDD8D2' }}
          >
            <span className="font-serif text-lg" style={{ color: '#1A1A1A' }}>
              All Results
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: '#8B8580' }}>
                {stats.length} models
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8B8580"
                strokeWidth="1.5"
                style={{
                  transform: showRankings ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                }}
              >
                <path
                  d="M6 9l6 6 6-6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>

          {showRankings && (
            <div className="space-y-1">
              {/* Header row */}
              <div
                className="flex items-center py-2 px-4 text-xs uppercase tracking-wider"
                style={{ color: '#8B8580' }}
              >
                <span className="w-8">#</span>
                <span className="flex-1 ml-10">Model</span>
                <span className="w-28 text-center">Record</span>
                <span className="ml-6 w-16 text-right">Wins</span>
              </div>

              {stats.map((stat, index) => {
                const isWinner = index === 0;

                return (
                  <div
                    key={stat.config_id}
                    className="flex items-center py-3 px-4 rounded-lg"
                    style={{
                      background: isWinner
                        ? 'rgba(139,35,50,0.06)'
                        : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <span
                      className="font-serif text-xl w-8 tabular-nums"
                      style={{ color: isWinner ? '#8B2332' : '#C0B8B0' }}
                    >
                      {index + 1}
                    </span>

                    <ProviderLogo
                      modelKey={getModelKeyFromName(stat.config_name)}
                      size={28}
                    />

                    <span
                      className="flex-1 ml-3 text-sm font-medium"
                      style={{ color: '#1A1A1A' }}
                    >
                      {stat.config_name.replace(' Baseline', '')}
                    </span>

                    <div
                      className="flex items-center gap-4 text-xs tabular-nums w-28 justify-center"
                      style={{ color: '#8B8580' }}
                    >
                      <span>{stat.wins}W</span>
                      <span>{stat.losses}L</span>
                      <span>{stat.ties + stat.both_bad}T</span>
                    </div>

                    <span
                      className="ml-6 text-sm font-semibold tabular-nums w-16 text-right"
                      style={{ color: isWinner ? '#8B2332' : '#6B6560' }}
                    >
                      {stat.wins}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div
          className="flex justify-center gap-4 mt-12 pb-8 fade-up"
          style={{ animationDelay: '400ms' }}
        >
          <button
            onClick={() => navigate(backUrl)}
            className="px-8 py-3 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: '#8B2332' }}
          >
            New Battle
          </button>
          <button
            onClick={() => navigate('/leaderboard')}
            className="px-8 py-3 rounded-lg text-sm font-medium transition-all hover:opacity-80"
            style={{
              background: 'transparent',
              color: '#6B6560',
              border: '1px solid #C5BEB6',
            }}
          >
            Leaderboard
          </button>
        </div>
      </div>
    </div>
  );
}
